/**
 * @fileoverview Invitation error card, shown when an invitation link can't lead to the
 * set-password form.
 * @description Visually mirrors ErrorStateCard's logo/badge/heading/description chrome, but can't
 * reuse it directly: ErrorStateCard's primary action is hardcoded to "Go to Home"
 * (authConfig.dashboardRoute), which is wrong for a visitor who, by definition, has no session
 * here. Two variants: "invalidLink" (the backend refused the link — unknown, expired, used or
 * revoked, which it deliberately doesn't tell apart — so it offers Log In for someone who already
 * set their password, and points everyone else at their administrator) and "unavailable" (the
 * check itself failed — rate limited, service down or offline — so it offers Try Again).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import logoUrl from '@/assets/images/login/logo.png'
import forbiddenIcon from '@/assets/svg/errors/icon-forbidden.svg'
import serverErrorIcon from '@/assets/svg/errors/icon-server-error.svg'
import Button from '@/shared/components/atoms/Button'
import { ROUTES } from '@/shared/constants/routes.constants'

interface InvitationErrorCardProps {
  variant: 'invalidLink' | 'unavailable'
  onRetry: () => void
}

/**
 * @component
 * @param {InvitationErrorCardProps} props - Which copy/action pair to render.
 * @param {'invalidLink' | 'unavailable'} props.variant - "invalidLink" when the backend refused the
 * link, "unavailable" when the check itself couldn't complete.
 * @param {() => void} props.onRetry - Re-runs the link check (the "unavailable" variant's action).
 */
function InvitationErrorCard({ variant, onRetry }: InvitationErrorCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div className="flex w-full flex-col items-center">
      <img src={logoUrl} alt="InsightBox" className="mb-2 h-18 sm:h-22 lg:h-24 w-auto" />
      <div className="mt-8 w-full rounded-[18px] border border-primary/10 bg-white p-6 text-center shadow-card sm:p-8 lg:p-10">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-error/10">
          <img
            src={variant === 'unavailable' ? serverErrorIcon : forbiddenIcon}
            alt=""
            className="h-7 w-7"
          />
        </div>
        <h1 className="mb-2.5 text-[24px] font-extrabold leading-[1.3] text-ink">
          {t(`acceptInvitation.errors.${variant}.title`)}
        </h1>
        <p className="mx-auto max-w-[320px] text-[14px] leading-[22px] text-slate">
          {t(`acceptInvitation.errors.${variant}.description`)}
        </p>
        <div className="mt-7 flex flex-col items-center gap-3">
          {variant === 'invalidLink' ? (
            <Button type="button" onClick={() => void navigate(ROUTES.LOGIN)}>
              {t('acceptInvitation.errors.loginAction')}
            </Button>
          ) : (
            <Button type="button" onClick={onRetry}>
              {t('acceptInvitation.errors.tryAgain')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default InvitationErrorCard
